import { useState } from "react";
import { Check, ChevronsUpDown, Link2, Link2Off, UserX } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { getDisplayFirstName } from "../utils/displayName";

export const QRLinkBadge = ({ qr }) => {
  if (qr.link_status === "linked") {
    return (
      <span data-testid={`qr-link-badge-${qr.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-semibold">
        <Link2 className="w-3 h-3" /> Linked · {getDisplayFirstName({ display_name: qr.linked_employee_display_name, name: qr.linked_employee_name })}
        {qr.linked_job_title ? <span className="text-emerald-400/70 font-normal">· {qr.linked_job_title}</span> : null}
      </span>
    );
  }
  if (qr.link_status === "inactive") {
    return (
      <span data-testid={`qr-link-badge-${qr.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 text-[11px] font-semibold">
        <UserX className="w-3 h-3" /> Unlinked · profile inactive
      </span>
    );
  }
  return (
    <span data-testid={`qr-link-badge-${qr.id}`} className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30 px-2 py-0.5 text-[11px] font-semibold">
      <Link2Off className="w-3 h-3" /> Unlinked
    </span>
  );
};

export const QRProfileLinkPicker = ({ qr, candidates, onLink }) => {
  const [open, setOpen] = useState(false);
  const current = candidates.find((c) => c.id === qr.linked_employee_id);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          data-testid={`qr-link-picker-${qr.id}`}
          className="border-slate-600 bg-slate-800/60 text-slate-200 hover:bg-slate-700 text-xs h-8 min-w-[190px] justify-between"
        >
          <span className="truncate">{current ? `${current.name}${current.job_title ? ` · ${current.job_title}` : ""}` : "Linked profile…"}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-slate-900 border-slate-700 text-slate-100" align="start">
        <Command className="bg-slate-900 text-slate-100">
          <CommandInput placeholder="Search employees…" data-testid={`qr-link-search-${qr.id}`} />
          <CommandList>
            <CommandEmpty>No active employee found.</CommandEmpty>
            <CommandGroup>
              {qr.linked_employee_id && (
                <CommandItem
                  value="__unlink__"
                  data-testid={`qr-unlink-option-${qr.id}`}
                  onSelect={() => { setOpen(false); onLink(qr, null); }}
                  className="text-rose-300"
                >
                  <Link2Off className="mr-2 h-3.5 w-3.5" /> Remove link
                </CommandItem>
              )}
              {candidates.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.display_name} ${c.job_title}`}
                  data-testid={`qr-link-option-${qr.id}-${c.id}`}
                  onSelect={() => { setOpen(false); if (c.id !== qr.linked_employee_id) onLink(qr, c.id); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${c.id === qr.linked_employee_id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{c.name}</span>
                  {c.job_title ? <span className="ml-auto text-[11px] text-slate-400">{c.job_title}</span> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
